import { useState, useRef, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { Box, IconButton } from "@mui/material";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import LoadingAnimation from "../Animations/LoadingAnimation";
import ImageLightbox from "./ImageLightbox";
import {
  cardOutline,
  imagesSliderNextIconButton,
  imagesSliderPrevIconButton,
} from "../../Styles/Styles";

const NewImagesSlider = ({ adDetails }) => {
  const [loading, setLoading] = useState(
    Array(adDetails.images.length).fill(true)
  );
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const handleImageLoad = (index) => {
    setLoading((prevLoading) => {
      const newLoading = [...prevLoading];
      newLoading[index] = false;
      return newLoading;
    });
  };

  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const swiperRef = useRef(null);

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.params.navigation.prevEl = prevRef.current;
      swiperRef.current.params.navigation.nextEl = nextRef.current;
      swiperRef.current.navigation.init();
      swiperRef.current.navigation.update();
    }
  }, []);

  return (
    <Box
      sx={{
        mt: 2,
        position: "relative",
        width: "100%",
        height: { xs: "auto", sm: "250px", md: "450px" },
        ...cardOutline,
      }}
    >
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        spaceBetween={10}
        slidesPerView={1}
        loop
        autoplay={{ delay: 3000, disableOnInteraction: false }}
        pagination={{ clickable: true }}
        navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
        onSwiper={(swiper) => (swiperRef.current = swiper)}
        style={{ backgroundColor: "#F1F3F4", width: "100%", height: "100%" }}
      >
        {adDetails.images?.length > 0 ? (
          adDetails.images.map((image, index) => (
            <SwiperSlide key={index}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  position: "relative",
                  overflow: "hidden",
                  borderRadius: "10px",
                }}
              >
                {loading[index] && <LoadingAnimation />}
                <Box
                  component="img"
                  src={image.thumbnail}
                  alt={adDetails.title}
                  onLoad={() => handleImageLoad(index)}
                  onClick={() => {
                    setLightboxIndex(index);
                    setLightboxOpen(true);
                  }}
                  sx={{
                    width: "100%",
                    height: "auto",
                    maxHeight: { xs: "250px", md: "450px" },
                    objectFit: "contain",
                    display: loading[index] ? "none" : "block",
                    transition: "opacity 0.5s ease-in-out",
                    opacity: loading[index] ? 0 : 1,
                    cursor: "pointer",
                  }}
                />
              </Box>
            </SwiperSlide>
          ))
        ) : (
          <SwiperSlide>
            <Box
              sx={{
                height: "450px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
                color: "#888",
                fontWeight: "bold",
              }}
            >
              No images available
            </Box>
          </SwiperSlide>
        )}
      </Swiper>

      {/* Custom Navigation Buttons */}
      {adDetails.images.length > 1 && (
        <>
          <IconButton
            ref={prevRef}
            aria-label="Previous Image"
            sx={{ ...imagesSliderPrevIconButton }}
          >
            <ArrowBackOutlinedIcon sx={{ fontSize: "24px" }} />
          </IconButton>

          <IconButton
            ref={nextRef}
            aria-label="Next Image"
            sx={{ ...imagesSliderNextIconButton }}
          >
            <ArrowForwardOutlinedIcon sx={{ fontSize: "24px" }} />
          </IconButton>
        </>
      )}

      {/* Lightbox */}
      {lightboxOpen && (
        <ImageLightbox
          images={adDetails.images}
          isOpen={lightboxOpen}
          startIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </Box>
  );
};

export default NewImagesSlider;
